import { Component, Element, Event, EventEmitter, h, Host, Prop, State, Watch } from "@stencil/core";
import { limitSlotElements } from "../../../utils/limitSlotElements";
import { logger } from "../../../utils/console";
import { SortDirection } from "../interfaces/directions";
import { UxSort } from "../interfaces/ux-sort";
import { WidthChangeEvent } from "../interfaces/width-change";

@Component({
    tag: "ux-table-cell-neo",
    styleUrls: ["table-cell-neo.scss"],
    shadow: true
})
export class TableRowNeo {
    @Element() host!: HTMLUxTableCellNeoElement;

    /**
     * If true, the cell will be rendered as a header cell.
     */
    @Prop() readonly head: boolean;

    /**
     * The number of columns this cell should span.
     */
    @Prop() readonly span: number;

    /**
     *  The key to sort by
     */
    @Prop() readonly sortkey: string;

    /**
     * If true, the cell will be sortable.
     */
    @Prop() readonly sortable: boolean;

    /**
     * If true, the cell will be resizable.
     */
    @Prop() readonly resizable: boolean;

    /**
     *  The current sort direction
     */
    @Prop() readonly sorted: SortDirection;

    /**
     * The index of the cell
     */
    @Prop() readonly index: number;

    /**
     * If true, the cell will be loading.
     */
    @Prop() readonly loading: boolean;

    componentDidLoad() {
        limitSlotElements(this.host, {
            allowed: {
                default: ["ux-table-row-neo"]
            }
        });
        this.handleSpanChange();
        this.cellWidth = this.host.clientWidth;
        this.handleResize();
    }

    private handleResize() {
        const resizeElement = this.host.shadowRoot?.querySelector("#resize");
        if (resizeElement) {
            let startX: number;
            let startWidth: number;
            const minWidth = getComputedStyle(this.host).getPropertyValue("--cell-min-width");
            const maxWidth = getComputedStyle(this.host).getPropertyValue("--cell-max-width");
            const onMouseMove = (event: MouseEvent) => {
                const newWidth = startWidth + (event.clientX - startX);
                if (newWidth < parseInt(minWidth, 10) || newWidth > parseInt(maxWidth, 10)) {
                    return;
                }
                this.widthChange.emit({ colIndex: this.index, width: newWidth });
            };

            const onMouseUp = () => {
                resizeElement.classList.remove("onresize");
                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
            };

            resizeElement.addEventListener("mousedown", (event: Event) => {
                const mouseEvent = event as MouseEvent;
                startX = mouseEvent.clientX;
                startWidth = this.host.clientWidth;
                document.addEventListener("mousemove", onMouseMove);
                document.addEventListener("mouseup", onMouseUp);
                resizeElement.classList.add("onresize");
            });

            resizeElement.addEventListener("keydown", (event: Event) => {
                const keyboardEvent = event as KeyboardEvent;
                if (keyboardEvent.key === "ArrowLeft" || keyboardEvent.key === "ArrowRight") {
                    const step = keyboardEvent.key === "ArrowLeft" ? -10 : 10;
                    const newWidth = this.host.clientWidth + step;
                    if (newWidth >= parseInt(minWidth, 10) && newWidth <= parseInt(maxWidth, 10)) {
                        this.widthChange.emit({ colIndex: this.index, width: newWidth });
                    }
                }
            });
        }
    }

    @State() cellWidth: number;

    /**
     * UXSort Event - Emits when the cell is sorted
     */
    @Event() uxSort: EventEmitter<UxSort>;

    /**
     * Handle span change and emit cell changed event
     */
    @Watch("span")
    handleSpanChange() {
        if (this.span > 0) {
            this.host.style.setProperty("--ux-cell-span", this.span.toString());
        }
    }

    @State() direction: SortDirection = "none";

    private handleSortChange(event: KeyboardEvent | MouseEvent) {
        if (!this.sortable) {
            return;
        }
        if (!this.sortkey) {
            logger.warn("No sort key provided for table cell");
            return;
        }
        if (event instanceof KeyboardEvent) {
            if (event.key !== "Enter") {
                return;
            }
        }
        this.direction = this.direction === "descending" ? "ascending" : this.direction === "ascending" ? "none" : "descending";
        this.uxSort.emit({ dir: this.direction, key: this.sortkey });
        this.host.setAttribute("aria-sort", this.direction);
        if (this.direction === "none") {
            this.host.removeAttribute("sorted");
            return;
        }
        this.host.setAttribute("sorted", this.direction);
    }

    /**
     * WidthChange Event - Emits when the width of the cell is changed
     */
    @Event() widthChange: EventEmitter<WidthChangeEvent>;

    render() {
        return (
            <Host role={this.head ? "columnheader" : "cell"} loading={this.loading}>
                {this.loading && <ux-skeleton></ux-skeleton>}
                {this.head && !this.loading && (
                    <div id="labelContainer" onKeyDown={(e) => this.handleSortChange(e)} tabIndex={0}>
                        <ux-copy variant="tiny" strong>
                            <slot />
                        </ux-copy>
                        {this.sortable && (
                            <ux-table-cell-sort-neo
                                onClick={(e) => this.handleSortChange(e)}
                                direction={this.direction}
                            ></ux-table-cell-sort-neo>
                        )}
                        {this.resizable && <button role="separator" aria-orientation="horizontal" id="resize"></button>}
                    </div>
                )}
                {!this.head && !this.loading && <slot />}
            </Host>
        );
    }
}
